import { IcAlert, IcAltitude, IcHeight } from '@assets/icons/24x24';
import { IcEmissionDetection, IcProcess } from '@assets/icons/32x32';
import { Center, For, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import DataSheetCardLink from '@components/common/layout/DataSheetCardLink';
import { useMapContext } from '@components/map/MapContext';
import { Tooltip } from '@components/ui/tooltip';
import { useAppSelector } from '@hooks/redux.hooks';
import { useSelectorWithReplayMode } from '@hooks/useSelectorWithReplayMode';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { hasAnyActionRoleOnSensors } from '@redux/authent/authent.selectors';
import { updateSelectedNotificationId } from '@redux/global/global.reducer';
import { selectSelectedNotificationId } from '@redux/global/global.selector';
import { updatePopupByMapId } from '@redux/maps/maps.reducer';
import { selectAltitudeUnit, selectHeightSuffix, selectHeightUnit } from '@redux/settings/settings.selectors';
import { selectSituationTime } from '@redux/situation/situation.selectors';
import { RootState } from '@redux/store';
import { formatDate, formatDurationFromTo } from '@utils/date.utils';
import { extendedSensorShortHand } from '@utils/map/map.constants';
import { convertDistanceUnitFromMeter, formatUnit } from '@utils/unit.utils';
import { format } from 'date-fns';
import { FormattedMessage, useIntl } from 'react-intl';

import { ProcedureStateEnum } from '@/types/config/procedure.types';
import { DroneNotificationLight, NotificationTypeEnum } from '@/types/data/data.types';
import { DateTimeEnum } from '@/types/dateTime.types';
import { PopupTypeEnum } from '@/types/popup.types';
import { SensorTypeEnum } from '@/types/sensor/sensors';

import CoordinatesInformation from '../../../common/CoordinatesInformation';
import PopupContainer from '../../../common/PopupContainer';
import DroneInformation from '../information/DroneInformation';
import { SEE_NOTIFICATIONS_ROLE } from '../TargetInfo';

type Props = {
  notification: DroneNotificationLight;
  position: { x: number; y: number };
};

export default function ExpiredDroneDetails({ notification, position }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const canSeeNotification = useAppSelector((state: RootState) =>
    hasAnyActionRoleOnSensors(state, SEE_NOTIFICATIONS_ROLE),
  );
  const showHeightSuffix = useAppSelector(selectHeightSuffix);
  const heightUnit = useAppSelector(selectHeightUnit);
  const altitudeUnit = useAppSelector(selectAltitudeUnit);
  const { mapId } = useMapContext();
  const updatePopup = useWithDispatch(updatePopupByMapId);
  const situationTime = useSelectorWithReplayMode(selectSituationTime);
  const selectedNotification = useAppSelector(selectSelectedNotificationId);
  const dispatchUpdateSelectedNotification = useWithDispatch(updateSelectedNotificationId);

  const formattedStartDate = formatDate(notification.startTime, DateTimeEnum.DATE);
  const formattedEndDate = formatDate(notification.endTime!, DateTimeEnum.DATE);
  const formattedStartTime = format(new Date(notification.startTime), "HH':'mm':'ss");
  const formattedEndTime = format(new Date(notification.endTime!), "HH':'mm':'ss");

  const isSameDay = formattedStartDate === formattedEndDate;

  const sensors = notification.trackIds.reduce<Partial<Record<SensorTypeEnum, string[]>>>(
    (acc, { sensorType, sensorCode }) => {
      if (!acc[sensorType]) {
        acc[sensorType] = [];
      }
      acc[sensorType].push(sensorCode);
      return acc;
    },
    {} as Partial<Record<SensorTypeEnum, string[]>>,
  );

  function handleHistoryPopup() {
    updatePopup({
      mapId,
      popup: {
        type: PopupTypeEnum.HISTORY,
        data: {
          identifiers: [notification.identifier],
          type: NotificationTypeEnum.DRONE,
          displayId: notification.displayId,
          openingDate: situationTime ? new Date(situationTime) : new Date(),
        },
        position,
      },
    });
  }

  return (
    <VStack gap={0.25} width="100%" height="100%">
      <DroneInformation notification={notification} />
      <PopupContainer>
        <VStack gap="standardXS" width="100%">
          <HStack width="100%" gap={3}>
            <HStack gap={1} flex={1}>
              <Icon as={IcHeight} boxSize={{ base: '20px', '4k': '24px' }} color="neutral.500" />
              <Text textStyle="screenStd" fontWeight="medium" color="neutral.500">
                <FormattedMessage id="global.last" />
              </Text>
              <Text textStyle="screenStd" fontWeight="medium">
                {formatUnit(heightUnit, convertDistanceUnitFromMeter(heightUnit, notification.currentHeight))}
              </Text>
              {showHeightSuffix && (
                <Text textStyle="screenStd" color="neutral.500">
                  <FormattedMessage id="target.agl" />
                </Text>
              )}
            </HStack>
            <HStack gap={1} flex={1}>
              <Icon as={IcHeight} boxSize={{ base: '20px', '4k': '24px' }} color="neutral.500" />
              <Text textStyle="screenStd" fontWeight="medium" color="neutral.500">
                <FormattedMessage id="global.max" />
              </Text>
              <Text textStyle="screenStd" fontWeight="medium">
                <HStack width="100%" gap={3}>
                  <HStack gap={1} flex={1}>
                    <Icon as={IcHeight} boxSize={{ base: '20px', '4k': '24px' }} color="neutral.500" />
                    <Text textStyle="screenStd" fontWeight="medium" color="neutral.500">
                      <FormattedMessage id="global.last" />
                    </Text>
                    <Text textStyle="screenStd" fontWeight="medium">
                      {formatUnit(heightUnit, convertDistanceUnitFromMeter(heightUnit, notification.currentHeight))}
                    </Text>
                    {showHeightSuffix && (
                      <Text textStyle="screenStd" color="neutral.500">
                        <FormattedMessage id="target.agl" />
                      </Text>
                    )}
                  </HStack>

                  <VStack gap={1} flex={1} alignItems="start">
                    <HStack gap={1}>
                      <Icon as={IcHeight} boxSize={{ base: '20px', '4k': '24px' }} color="neutral.500" />
                      <Text textStyle="screenStd" fontWeight="medium" color="neutral.500">
                        <FormattedMessage id="targetInfos.tab.information.elevation.max.transmitted" />
                      </Text>
                      <Text textStyle="screenStd" fontWeight="medium">
                        {notification.maxTransmittedAgl != null ? (
                          formatUnit(
                            heightUnit,
                            convertDistanceUnitFromMeter(heightUnit, notification.maxTransmittedAgl),
                          )
                        ) : (
                          <FormattedMessage id="global.NA" />
                        )}
                      </Text>
                      {showHeightSuffix && (
                        <Text textStyle="screenStd" color="neutral.500">
                          <FormattedMessage id="target.agl" />
                        </Text>
                      )}
                    </HStack>

                    <HStack gap={1}>
                      <Icon as={IcHeight} boxSize={{ base: '20px', '4k': '24px' }} color="neutral.500" />
                      <Text textStyle="screenStd" fontWeight="medium" color="neutral.500">
                        <FormattedMessage id="targetInfos.tab.information.elevation.max.computed" />
                      </Text>
                      <Text textStyle="screenStd" fontWeight="medium">
                        {notification.maxComputedAgl != null ? (
                          formatUnit(heightUnit, convertDistanceUnitFromMeter(heightUnit, notification.maxComputedAgl))
                        ) : (
                          <FormattedMessage id="global.NA" />
                        )}
                      </Text>
                      {showHeightSuffix && (
                        <Text textStyle="screenStd" color="neutral.500">
                          <FormattedMessage id="target.agl" />
                        </Text>
                      )}
                    </HStack>

                    <HStack gap={1}>
                      <Icon as={IcAltitude} boxSize={{ base: '20px', '4k': '24px' }} color="neutral.500" />
                      <Text textStyle="screenStd" fontWeight="medium" color="neutral.500">
                        <FormattedMessage id="targetInfos.tab.information.elevation.max.transmitted" />
                      </Text>
                      <Text textStyle="screenStd" fontWeight="medium">
                        {notification.maxTransmittedAsl != null ? (
                          formatUnit(
                            altitudeUnit,
                            convertDistanceUnitFromMeter(altitudeUnit, notification.maxTransmittedAsl),
                          )
                        ) : (
                          <FormattedMessage id="global.NA" />
                        )}
                      </Text>
                      {showHeightSuffix && (
                        <Text textStyle="screenStd" color="neutral.500">
                          <FormattedMessage id="target.amsl" />
                        </Text>
                      )}
                    </HStack>

                    <HStack gap={1}>
                      <Icon as={IcAltitude} boxSize={{ base: '20px', '4k': '24px' }} color="neutral.500" />
                      <Text textStyle="screenStd" fontWeight="medium" color="neutral.500">
                        <FormattedMessage id="targetInfos.tab.information.elevation.max.computed" />
                      </Text>
                      <Text textStyle="screenStd" fontWeight="medium">
                        {notification.maxComputedAsl != null ? (
                          formatUnit(
                            altitudeUnit,
                            convertDistanceUnitFromMeter(altitudeUnit, notification.maxComputedAsl),
                          )
                        ) : (
                          <FormattedMessage id="global.notComputed" />
                        )}
                      </Text>
                      {showHeightSuffix && (
                        <Text textStyle="screenStd" color="neutral.500">
                          <FormattedMessage id="target.amsl" />
                        </Text>
                      )}
                    </HStack>
                  </VStack>
                </HStack>
              </Text>
              {showHeightSuffix && (
                <Text textStyle="screenStd" color="neutral.500">
                  <FormattedMessage id="target.agl" />
                </Text>
              )}
            </HStack>
          </HStack>
          <HStack width="100%" gap={3}>
            <HStack gap={1} flex={1}>
              <Icon as={IcEmissionDetection} boxSize={{ base: '20px', '4k': '24px' }} color="neutral.500" />
              <For each={Object.entries(sensors)}>
                {([key, values]) => (
                  <Tooltip
                    key={key}
                    content={
                      <VStack gap={0.25}>
                        {values.map((code) => (
                          <Text
                            paddingX={1.5}
                            paddingY={1}
                            key={code}
                            textStyle="screenStd"
                            backgroundColor="neutral.black"
                          >
                            {code}
                          </Text>
                        ))}
                      </VStack>
                    }
                  >
                    <Text
                      paddingX={0.5}
                      paddingY={0.25}
                      key={key}
                      userSelect="none"
                      textStyle="screenStd"
                      color="neutral.300"
                      backgroundColor="neutral.black"
                    >
                      {extendedSensorShortHand[key as SensorTypeEnum]}
                    </Text>
                  </Tooltip>
                )}
              </For>
            </HStack>
          </HStack>
        </VStack>
      </PopupContainer>
      <HStack gap={0.25} width="100%" height="76px" cursor="default">
        <Center height="100%" flex={1} backgroundColor="neutral.700">
          <Text textStyle="screenStd">{formatDurationFromTo(notification.startTime, notification.endTime!)}</Text>
        </Center>
        <Center height="100%" flex={1} backgroundColor="neutral.700">
          <Text color="neutral.300" textStyle="screenStd" whiteSpace="pre-line">
            <FormattedMessage
              id={isSameDay ? 'targetInfos.tab.information.sameDay' : 'targetInfos.tab.information.multipleDays'}
              values={{
                startDate: (
                  <Text as="span" key="formattedStartDate" textStyle="screenStd" color="neutral.white">
                    {formattedStartDate}
                  </Text>
                ),
                startTime: (
                  <Text as="span" key="formattedStartTime" textStyle="screenStd" color="neutral.white">
                    {formattedStartTime}
                  </Text>
                ),
                endDate: (
                  <Text as="span" key="formattedEndDate" textStyle="screenStd" color="neutral.white">
                    {formattedEndDate}
                  </Text>
                ),
                endTime: (
                  <Text as="span" key="formattedEndTime" textStyle="screenStd" color="neutral.white">
                    {formattedEndTime}
                  </Text>
                ),
              }}
            />
          </Text>
        </Center>
      </HStack>
      <CoordinatesInformation coordinates={notification.position} prefix={formatMessage({ id: 'global.last' })} />
      {notification.procedureState !== ProcedureStateEnum.DONE && (
        <DataSheetCardLink
          label={formatMessage({ id: 'targetInfos.tab.information.process' })}
          optionalLabel={formatMessage(
            { id: 'targetInfos.tab.information.state' },
            { state: notification.procedureState },
          )}
          icon={IcProcess}
          isDisabled={selectedNotification === notification.id}
          onClick={() => dispatchUpdateSelectedNotification(notification.id)}
        />
      )}
      {canSeeNotification && (
        <DataSheetCardLink
          label={formatMessage({ id: 'targetInfos.tab.information.history' })}
          icon={IcAlert}
          onClick={handleHistoryPopup}
        />
      )}
    </VStack>
  );
}
